import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { useBreadcrumbTitle } from '../../components/layout/Breadcrumbs'
import { CardSkeletonGrid, EmptyState, ErrorState, Section } from '../../components/common/States'
import { NotFoundPage } from '../../components/common/NotFoundPage'
import { AttractionCard } from './AttractionCard'
import { HotelCard } from '../hotels/HotelCard'
import { contentApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { ApiError } from '../../api/client'

/**
 * "3 h 50 min drive", not "230 min drive".
 *
 * Minutes are the right unit up to about an hour and a half; past that nobody converts in
 * their head, and Helsinki at 230 minutes reads as a typo rather than as an afternoon.
 * The threshold is 90 rather than 60 so that "75 min" stays as it is - it is still a number
 * people think in.
 */
function driveTimeLabel(minutes: number, t: TFunction): string {
  if (minutes < 90) {
    return t('explore.driveTime', { count: minutes })
  }
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0
    ? t('explore.driveTimeHoursExact', { count: hours })
    : t('explore.driveTimeHours', { hours, minutes: rest })
}

export function DestinationPage() {
  const { slug = '' } = useParams()
  const { t } = useTranslation()
  const { locale } = useLocale()

  const destination = useQuery({
    queryKey: ['destination', slug, locale],
    queryFn: () => contentApi.destination(slug, locale),
  })

  // Above the early returns - hooks cannot follow a conditional return.
  useBreadcrumbTitle(destination.data?.name)

  if (destination.isError && destination.error instanceof ApiError && destination.error.status === 404) {
    return <NotFoundPage />
  }

  if (destination.isPending) {
    return (
      <Container sx={{ py: 8 }}>
        <CardSkeletonGrid count={3} />
      </Container>
    )
  }

  if (destination.isError || !destination.data) {
    return (
      <Container sx={{ py: 8 }}>
        <ErrorState error={destination.error} onRetry={() => void destination.refetch()} />
      </Container>
    )
  }

  const place = destination.data

  return (
    <>
      <PageMeta
        title={place.name}
        description={place.summary ?? undefined}
        image={place.heroImageUrl}
        path={`/explore/${place.slug}`}
      />

      <Box sx={{ bgcolor: 'primary.dark', color: 'common.white' }}>
        <Container sx={{ py: { xs: 6, md: 9 } }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            {place.region && (
              <Chip
                size="small"
                label={place.region}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'inherit' }}
              />
            )}
            {place.distanceKm != null && (
              <Chip
                size="small"
                label={`🚗 ${t('explore.kmAway', { count: place.distanceKm })}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'inherit' }}
              />
            )}
            {place.driveTimeMinutes != null && (
              <Chip
                size="small"
                label={`⏱ ${driveTimeLabel(place.driveTimeMinutes, t)}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'inherit' }}
              />
            )}
          </Stack>

          <Typography variant="h1" sx={{ mb: 2 }}>
            {place.name}
          </Typography>
          <Typography variant="subtitle1" sx={{ maxWidth: 720, color: 'rgba(255,255,255,0.86)' }}>
            {place.summary}
          </Typography>
        </Container>
      </Box>

      {place.description && (
        <Container sx={{ pt: { xs: 5, md: 7 } }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 760, whiteSpace: 'pre-line' }}
          >
            {place.description}
          </Typography>

          {place.latitude && place.longitude && (
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-block', mt: 2 }}
            >
              📍 {t('contact.openInMaps')}
            </Link>
          )}
        </Container>
      )}

      <Section title={t('explore.attractionsIn', { name: place.name })}>
        {place.attractions.length === 0 ? (
          <EmptyState icon="🗺️" title={t('explore.noResults')} />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            }}
          >
            {place.attractions.map((attraction) => (
              <AttractionCard key={attraction.id} attraction={attraction} />
            ))}
          </Box>
        )}
      </Section>

      {place.hotels.length > 0 && (
        <Box sx={{ bgcolor: 'background.paper' }}>
          <Section title={t('explore.hotelsIn', { name: place.name })}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              }}
            >
              {place.hotels.map((hotel) => (
                <HotelCard key={hotel.id} hotel={hotel} />
              ))}
            </Box>
          </Section>
        </Box>
      )}
    </>
  )
}
